/**
 * Radar / spider chart over the buyer-profile match categories.
 * One axis per `DealCategory` in `CATEGORY_DISPLAY_ORDER` (seven as of v4);
 * values are the engine's `spiderValue` (0–1, already multiplied by the
 * exclusion factor).
 *
 * Mirrors the codebase's chart.js pattern (see MedianIrrTrendChart.tsx):
 * register only the controllers we use at module scope, hold the canvas in a
 * ref, destroy + recreate on data change.
 */
import { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  Filler,
  LineElement,
  PointElement,
  RadarController,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';
import { CategoryBreakdown } from '../types';
import { buildSpiderData } from './buildSpiderData';

ChartJS.register(
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip
);

interface Props {
  categories: CategoryBreakdown[];
}

export default function SpiderChart({ categories }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<ChartJS | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const data = buildSpiderData(categories);

    chartRef.current?.destroy();
    chartRef.current = new ChartJS(ctx, {
      type: 'radar',
      data: {
        labels: data.map((d) => d.label),
        datasets: [
          {
            label: 'Match',
            data: data.map((d) => d.value),
            backgroundColor: 'rgba(33, 133, 208, 0.15)',
            borderColor: 'rgba(33, 133, 208, 0.9)',
            pointBackgroundColor: 'rgba(33, 133, 208, 1)',
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: true,
            borderJoinStyle: 'round',
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctxItem) => {
                const datum = data[ctxItem.dataIndex];
                if (datum.empty) {
                  return `${datum.label}: buyer profile incomplete`;
                }
                const b = datum.breakdown!;
                return `${datum.label}: ${b.percent}% · ${b.exclusion} excl pts → ${datum.value.toFixed(2)}`;
              },
            },
          },
        },
        scales: {
          r: {
            min: 0,
            max: 1,
            ticks: {
              stepSize: 0.5,
              callback: (v) => (typeof v === 'number' ? v.toFixed(1) : v),
            },
            pointLabels: { font: { size: 12 } },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [categories]);

  return (
    <div className="buyer-profile-match__spider">
      <canvas ref={canvasRef} />
    </div>
  );
}
